import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Loader2, 
  FileText, 
  Copy, 
  Calendar,
  Sparkles,
  CheckCircle2,
  Layers,
  Eye,
  ChevronRight,
  ChevronLeft,
  X,
  Search,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 5;

const ContentGenerator = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('single'); // 'single', 'planner', 'history'
  const [loading, setLoading] = useState(false);
  const [activeBusiness, setActiveBusiness] = useState(null);
  
  // Single content form
  const [contentType, setContentType] = useState('linkedin_post');
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('linkedin');
  
  // Planner form
  const [plannerTopic, setPlannerTopic] = useState('');
  const [plannerStep, setPlannerStep] = useState(1); // 1 or 2
  const [plannerIdeas, setPlannerIdeas] = useState(null);
  const [plannerContent, setPlannerContent] = useState(null);
  
  // Results
  const [contentResult, setContentResult] = useState(null);
  
  // History - now separated
  const [individualContent, setIndividualContent] = useState([]);
  const [plannerProjects, setPlannerProjects] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null); // For viewing project details

  // Pagination states
  const [plannerPage, setPlannerPage] = useState(1);
  const [individualPage, setIndividualPage] = useState(1);

  // Search states
  const [plannerSearch, setPlannerSearch] = useState('');
  const [individualSearch, setIndividualSearch] = useState('');

  // Legacy history for backwards compatibility
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadBusiness();
    loadHistory();
  }, []);

  const loadBusiness = async () => {
    try {
      const response = await apiClient.get('/user/active-business');
      setActiveBusiness(response.data.business);
    } catch (error) {
      console.error('Error loading business:', error);
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      // Load both individual content and planner projects
      const [individualRes, plannerRes] = await Promise.all([
        apiClient.get('/content/individual'),
        apiClient.get('/planner/projects')
      ]);
      setIndividualContent(individualRes.data);
      setPlannerProjects(plannerRes.data);
      // Keep legacy history for backwards compatibility
      setHistory([...individualRes.data, ...plannerRes.data]);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerateSingle = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await apiClient.post('/content/generate', {
        content_type: contentType,
        topic,
        platform
      });
      
      setContentResult(response.data);
      toast.success('¡Contenido generado!');
      loadHistory();
      
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Error al generar contenido');
    } finally {
      setLoading(false);
    }
  };

  const handlePlannerStep1 = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await apiClient.post('/planner/step-1', {
        business_context: plannerTopic
      });
      
      setPlannerIdeas(response.data);
      setPlannerStep(2);
      toast.success('¡9 ideas generadas!');
      
    } catch (error) {
      console.error('Error in planner step 1:', error);
      toast.error('Error al generar ideas');
    } finally {
      setLoading(false);
    }
  };

  const handlePlannerStep2 = async () => {
    try {
      setLoading(true);
      
      // Get the project_id from step 1 response
      const projectId = plannerIdeas.project_id;
      
      const response = await apiClient.post(`/planner/step-2/${projectId}`);
      
      setPlannerContent(response.data);
      toast.success('¡27 piezas de contenido generadas!');
      loadHistory();
      
    } catch (error) {
      console.error('Error in planner step 2:', error);
      toast.error('Error al generar contenido');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Copiado al portapapeles');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Copiado al portapapeles');
      } catch (e) {
        toast.error('No se pudo copiar');
      }
      document.body.removeChild(textArea);
    }
  };

  const renderSingleTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generar Contenido Individual</CardTitle>
          <CardDescription>
            Crea posts, artículos, newsletters personalizados para tu negocio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleGenerateSingle} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contentType">Tipo de Contenido *</Label>
                <select
                  id="contentType"
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="linkedin_post">Post de LinkedIn</option>
                  <option value="blog_article">Artículo de Blog</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="social_post">Post de Redes Sociales</option>
                  <option value="email">Email Marketing</option>
                </select>
              </div>

              <div>
                <Label htmlFor="platform">Plataforma *</Label>
                <select
                  id="platform"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="linkedin">LinkedIn</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="twitter">Twitter/X</option>
                  <option value="blog">Blog/Website</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="topic">Tema o Idea *</Label>
              <Textarea
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Ej: Cómo aumentar productividad con IA, Tendencias de marketing 2024..."
                rows={3}
                required
              />
            </div>

            <Button type="submit" className="w-full h-12" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Generando contenido...
                </>
              ) : (
                <>
                  <FileText className="mr-2 w-5 h-5" />
                  Generar Contenido
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {contentResult && (
        <Card className="border-2 border-green-300">
          <CardHeader className="bg-green-50">
            <div className="flex items-center justify-between">
              <CardTitle>Contenido Generado</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  // Get the main text content to copy
                  const textToCopy = typeof contentResult.content === 'object' 
                    ? (contentResult.content.main_text || contentResult.content.caption || contentResult.content.intro || JSON.stringify(contentResult.content, null, 2))
                    : (contentResult.content || contentResult.post_content || '');
                  handleCopy(textToCopy);
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {/* Hook */}
            {contentResult.content?.hook && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-sm text-yellow-700 mb-2">🎯 Hook (Primera línea):</h4>
                <p className="text-slate-900 font-medium">{contentResult.content.hook}</p>
              </div>
            )}
            
            {/* Main Content */}
            <div>
              <h4 className="font-semibold text-sm text-slate-700 mb-2">📝 Contenido Principal:</h4>
              <div className="bg-white p-4 rounded border">
                <p className="text-slate-900 whitespace-pre-wrap">
                  {typeof contentResult.content === 'object' 
                    ? (contentResult.content.main_text || contentResult.content.caption || contentResult.content.intro || '')
                    : (contentResult.content || contentResult.post_content || '')}
                </p>
              </div>
            </div>

            {/* CTA */}
            {contentResult.content?.cta && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-sm text-blue-700 mb-2">👉 Llamado a la Acción:</h4>
                <p className="text-slate-900 font-medium">{contentResult.content.cta}</p>
              </div>
            )}

            {/* Tweets (for Twitter threads) */}
            {contentResult.content?.tweets && (
              <div>
                <h4 className="font-semibold text-sm text-slate-700 mb-2">🐦 Hilo de Twitter ({contentResult.content.tweets.length} tweets):</h4>
                <div className="space-y-2">
                  {contentResult.content.tweets.map((tweet, idx) => (
                    <div key={idx} className="bg-slate-50 p-3 rounded border">
                      <span className="text-xs font-bold text-slate-500">Tweet {tweet.number || idx + 1}:</span>
                      <p className="text-slate-900 text-sm mt-1">{tweet.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subject lines (for emails) */}
            {contentResult.content?.subjects && (
              <div>
                <h4 className="font-semibold text-sm text-slate-700 mb-2">📧 Asuntos de Email:</h4>
                <div className="space-y-2">
                  {contentResult.content.subjects.map((subject, idx) => (
                    <div key={idx} className="bg-slate-50 p-2 rounded border flex items-center justify-between">
                      <p className="text-slate-900 text-sm">{subject}</p>
                      <Button size="sm" variant="ghost" onClick={() => handleCopy(subject)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ad variations */}
            {contentResult.content?.variations && (
              <div>
                <h4 className="font-semibold text-sm text-slate-700 mb-2">📢 Variaciones de Anuncio:</h4>
                <div className="space-y-2">
                  {contentResult.content.variations.map((variation, idx) => (
                    <div key={idx} className="bg-slate-50 p-3 rounded border">
                      <p className="text-sm font-bold text-slate-700">{variation.headline}</p>
                      <p className="text-slate-600 text-sm mt-1">{variation.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hashtags */}
            {(contentResult.content?.hashtags || contentResult.hashtags) && (
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-sm text-purple-700 mb-2"># Hashtags:</h4>
                <div className="flex flex-wrap gap-2">
                  {(contentResult.content?.hashtags || contentResult.hashtags || []).map((tag, idx) => (
                    <span 
                      key={idx} 
                      className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm cursor-pointer hover:bg-purple-200"
                      onClick={() => handleCopy(tag.startsWith('#') ? tag : `#${tag}`)}
                    >
                      {tag.startsWith('#') ? tag : `#${tag}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderPlannerTab = () => (
    <div className="space-y-6">
      {plannerStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Planificador 3-3-3: Paso 1
            </CardTitle>
            <CardDescription>
              MÓNICA generará 9 ideas pilares para tu contenido mensual
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePlannerStep1} className="space-y-6">
              {/* Mostrar contexto del negocio activo */}
              {activeBusiness && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Negocio Activo
                  </h4>
                  <p className="text-sm text-purple-800">
                    {activeBusiness.business_name || activeBusiness.business_description?.substring(0, 100) + '...'}
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    El contenido se generará basado en el contexto de tu negocio y tu ICP
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="plannerTopic">🎯 Tema o enfoque para este mes *</Label>
                <Textarea
                  id="plannerTopic"
                  value={plannerTopic}
                  onChange={(e) => setPlannerTopic(e.target.value)}
                  placeholder="Ej: Promoción de fin de año, Lanzamiento de nuevo producto, Contenido educativo sobre migración..."
                  rows={3}
                  required
                />
                <p className="text-xs text-slate-500 mt-2">
                  ¿Qué tema específico quieres cubrir en tu contenido este mes?
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">📅 Metodología 3-3-3:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✅ Paso 1: 9 ideas pilares de contenido</li>
                  <li>✅ Paso 2: 3 piezas de contenido por idea (9x3 = 27 piezas)</li>
                  <li>✅ Resultado: Calendario completo para 30 días</li>
                </ul>
              </div>

              <Button type="submit" className="w-full h-12" disabled={loading || !activeBusiness}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Generando 9 ideas...
                  </>
                ) : !activeBusiness ? (
                  <>
                    <Sparkles className="mr-2 w-5 h-5" />
                    Primero crea un ICP
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 w-5 h-5" />
                    Generar 9 Ideas Pilares
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {plannerStep === 2 && plannerIdeas && !plannerContent && (
        <div className="space-y-6">
          <Card className="border-2 border-purple-300">
            <CardHeader className="bg-purple-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>9 Ideas Pilares Generadas</CardTitle>
                  <CardDescription>
                    Estas son las ideas base para tu contenido mensual
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setPlannerStep(1);
                    setPlannerIdeas(null);
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver a generar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid gap-3">
                {(plannerIdeas.mother_ideas || plannerIdeas.core_ideas || []).map((idea, idx) => (
                  <Card key={idx} className={`bg-white border-l-4 ${
                    idea.type === 'educativa' ? 'border-l-blue-500' :
                    idea.type === 'credibilidad' ? 'border-l-green-500' :
                    'border-l-pink-500'
                  }`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full text-white flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                          idea.type === 'educativa' ? 'bg-blue-500' :
                          idea.type === 'credibilidad' ? 'bg-green-500' :
                          'bg-pink-500'
                        }`}>
                          {idea.id || idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              idea.type === 'educativa' ? 'bg-blue-100 text-blue-700' :
                              idea.type === 'credibilidad' ? 'bg-green-100 text-green-700' :
                              'bg-pink-100 text-pink-700'
                            }`}>
                              {idea.type === 'educativa' ? '📚 Educativa' :
                               idea.type === 'credibilidad' ? '🏆 Credibilidad' :
                               '💝 Conexión'}
                            </span>
                          </div>
                          <p className="font-semibold text-slate-900">{idea.title}</p>
                          <p className="text-sm text-slate-600 mt-1">{idea.description}</p>
                          {idea.key_message && (
                            <p className="text-xs text-slate-500 mt-2 italic">💡 {idea.key_message}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="pt-6 text-center">
              <Layers className="w-12 h-12 text-blue-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-blue-900 mb-2">
                ¡Listo para Paso 2!
              </h3>
              <p className="text-blue-800 mb-4">
                Ahora generaré 3 piezas de contenido por cada idea (27 piezas totales)
              </p>
              <Button onClick={handlePlannerStep2} disabled={loading} size="lg">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Generando 27 piezas...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 w-5 h-5" />
                    Generar 27 Piezas de Contenido
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {plannerContent && (
        <div className="space-y-6">
          <Card className="border-2 border-green-300">
            <CardHeader className="bg-green-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>📅 {plannerContent.content_pieces?.length || 27} Piezas de Contenido</CardTitle>
                  <CardDescription>
                    Tu calendario completo de contenido para el mes
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setPlannerStep(1);
                    setPlannerIdeas(null);
                    setPlannerContent(null);
                    setPlannerTopic('');
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Nuevo Plan
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                {plannerContent.content_pieces?.map((piece, idx) => (
                  <Card key={idx} className={`hover:shadow-md transition-shadow border-l-4 ${
                    piece.format === 'Reel' ? 'border-l-pink-500' :
                    piece.format === 'Carrusel' ? 'border-l-blue-500' :
                    'border-l-purple-500'
                  }`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="px-2 py-1 bg-slate-800 text-white text-xs font-bold rounded">
                              Día {piece.day || idx + 1}
                            </span>
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              piece.format === 'Reel' ? 'bg-pink-100 text-pink-800' :
                              piece.format === 'Carrusel' ? 'bg-blue-100 text-blue-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {piece.format || 'Contenido'}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded ${
                              piece.idea_type === 'educativa' ? 'bg-blue-50 text-blue-700' :
                              piece.idea_type === 'credibilidad' ? 'bg-green-50 text-green-700' :
                              'bg-pink-50 text-pink-700'
                            }`}>
                              {piece.idea_type || 'general'}
                            </span>
                          </div>
                          <p className="font-semibold text-slate-900">{piece.title}</p>
                          {piece.hook && (
                            <p className="text-sm text-yellow-700 mt-1 font-medium">🎯 {piece.hook}</p>
                          )}
                          <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{piece.content}</p>
                          {piece.cta && (
                            <p className="text-sm text-blue-600 mt-2 font-medium">👉 {piece.cta}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(`${piece.title}\n\n🎯 Hook: ${piece.hook || ''}\n\n${piece.content}\n\n👉 CTA: ${piece.cta || ''}`)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-green-900 mb-2">
                ¡Calendario de Contenido Completo!
              </h3>
              <p className="text-green-800 mb-4">
                {plannerContent.content_pieces?.length || 27} piezas guardadas automáticamente • Listas para publicar
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => {
                  setPlannerStep(1);
                  setPlannerIdeas(null);
                  setPlannerContent(null);
                  setPlannerTopic('');
                }} variant="outline">
                  Crear Otro Plan
                </Button>
                <Button onClick={() => setActiveTab('history')}>
                  Ver Historial
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  // Render project detail modal
  const renderProjectDetailModal = () => {
    if (!selectedProject) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 pt-20 overflow-y-auto" onClick={() => setSelectedProject(null)}>
        <div 
          className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10 rounded-t-xl">
            <div>
              <h3 className="font-bold text-lg text-slate-900">Planificación 3-3-3</h3>
              <p className="text-sm text-slate-600">{selectedProject.topic}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                {selectedProject.content_pieces?.length || 0} piezas
              </span>
              <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Mother Ideas Summary */}
          {selectedProject.mother_ideas && (
            <div className="px-6 py-4 bg-purple-50 border-b">
              <h4 className="font-semibold text-purple-900 mb-3">9 Ideas Pilares:</h4>
              <div className="grid grid-cols-3 gap-2">
                {selectedProject.mother_ideas.map((idea, idx) => (
                  <div key={idx} className={`p-2 rounded text-xs ${
                    idea.type === 'educativa' ? 'bg-blue-100 text-blue-800' :
                    idea.type === 'credibilidad' ? 'bg-green-100 text-green-800' :
                    'bg-pink-100 text-pink-800'
                  }`}>
                    <span className="font-semibold">{idx + 1}.</span> {idea.title}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Pieces */}
          <div className="p-6 space-y-4">
            <h4 className="font-semibold text-slate-900">Contenido Generado:</h4>
            {selectedProject.content_pieces?.map((piece, idx) => (
              <Card key={idx} className={`border-l-4 ${
                piece.format === 'Reel' ? 'border-l-pink-500' :
                piece.format === 'Carrusel' ? 'border-l-blue-500' :
                'border-l-purple-500'
              }`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2 py-1 bg-slate-800 text-white text-xs font-bold rounded">
                          Día {piece.day || idx + 1}
                        </span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          piece.format === 'Reel' ? 'bg-pink-100 text-pink-800' :
                          piece.format === 'Carrusel' ? 'bg-blue-100 text-blue-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {piece.format || 'Contenido'}
                        </span>
                        {piece.idea_type && (
                          <span className={`px-2 py-1 text-xs rounded ${
                            piece.idea_type === 'educativa' ? 'bg-blue-50 text-blue-700' :
                            piece.idea_type === 'credibilidad' ? 'bg-green-50 text-green-700' :
                            'bg-pink-50 text-pink-700'
                          }`}>
                            {piece.idea_type}
                          </span>
                        )}
                      </div>
                      <p className="font-semibold text-slate-900">{piece.title}</p>
                      {piece.hook && (
                        <p className="text-sm text-yellow-700 mt-1 font-medium">🎯 {piece.hook}</p>
                      )}
                      <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{piece.content}</p>
                      {piece.cta && (
                        <p className="text-sm text-blue-600 mt-2 font-medium">👉 {piece.cta}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy(`${piece.title}\n\n🎯 Hook: ${piece.hook || ''}\n\n${piece.content}\n\n👉 CTA: ${piece.cta || ''}`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end rounded-b-xl">
            <Button onClick={() => setSelectedProject(null)}>
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderHistoryTab = () => {
    // Filter by search
    const filteredPlannerProjects = plannerProjects.filter(project => 
      plannerSearch === '' || 
      project.topic?.toLowerCase().includes(plannerSearch.toLowerCase()) ||
      project.mother_ideas?.some(idea => 
        idea.title?.toLowerCase().includes(plannerSearch.toLowerCase()) ||
        idea.description?.toLowerCase().includes(plannerSearch.toLowerCase())
      )
    );

    const filteredIndividualContent = individualContent.filter(item => {
      if (individualSearch === '') return true;
      const searchLower = individualSearch.toLowerCase();
      const contentText = typeof item.content === 'string' 
        ? item.content 
        : (item.content?.main_text || item.content?.caption || item.content?.intro || '');
      return (
        item.topic?.toLowerCase().includes(searchLower) ||
        item.content_type?.toLowerCase().includes(searchLower) ||
        item.platform?.toLowerCase().includes(searchLower) ||
        contentText.toLowerCase().includes(searchLower)
      );
    });

    // Pagination calculations (after filtering)
    const plannerTotalPages = Math.ceil(filteredPlannerProjects.length / ITEMS_PER_PAGE) || 1;
    const plannerStartIdx = (plannerPage - 1) * ITEMS_PER_PAGE;
    const paginatedPlannerProjects = filteredPlannerProjects.slice(plannerStartIdx, plannerStartIdx + ITEMS_PER_PAGE);

    const individualTotalPages = Math.ceil(filteredIndividualContent.length / ITEMS_PER_PAGE) || 1;
    const individualStartIdx = (individualPage - 1) * ITEMS_PER_PAGE;
    const paginatedIndividualContent = filteredIndividualContent.slice(individualStartIdx, individualStartIdx + ITEMS_PER_PAGE);

    // Reset to page 1 when search changes
    const handlePlannerSearch = (value) => {
      setPlannerSearch(value);
      setPlannerPage(1);
    };

    const handleIndividualSearch = (value) => {
      setIndividualSearch(value);
      setIndividualPage(1);
    };

    const SearchInput = ({ value, onChange, placeholder }) => (
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="pl-10 h-9"
        />
      </div>
    );

    const PaginationControls = ({ currentPage, totalPages, setPage, totalItems }) => (
      <div className="flex items-center justify-between pt-4 border-t mt-4">
        <span className="text-sm text-slate-500">
          {totalItems > 0 
            ? `Mostrando ${Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalItems)}-${Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} de ${totalItems}`
            : 'Sin resultados'
          }
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium px-3">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Planificaciones 3-3-3 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-purple-600" />
                  Planificaciones 3-3-3
                </CardTitle>
                <CardDescription>
                  Calendarios mensuales completos: {plannerProjects.length} proyectos
                </CardDescription>
              </div>
              {plannerProjects.length > 0 && (
                <div className="w-64">
                  <SearchInput 
                    value={plannerSearch}
                    onChange={handlePlannerSearch}
                    placeholder="Buscar planificaciones..."
                  />
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
              </div>
            ) : plannerProjects.length === 0 ? (
              <div className="text-center py-6 bg-purple-50 rounded-lg">
                <Sparkles className="w-10 h-10 text-purple-300 mx-auto mb-2" />
                <p className="text-purple-600 text-sm">Aún no has creado planificaciones 3-3-3</p>
                <Button onClick={() => setActiveTab('planner')} variant="outline" size="sm" className="mt-3">
                  Crear Primera Planificación
                </Button>
              </div>
            ) : filteredPlannerProjects.length === 0 ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg">
                <Search className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-600 text-sm">No se encontraron resultados para "{plannerSearch}"</p>
                <Button onClick={() => setPlannerSearch('')} variant="outline" size="sm" className="mt-3">
                  Limpiar búsqueda
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {paginatedPlannerProjects.map((project, idx) => (
                    <Card 
                      key={idx} 
                      className="hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer group"
                      onClick={() => setSelectedProject(project)}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded">
                                Planificación 3-3-3
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(project.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="font-semibold text-slate-900">{project.topic}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>📋 {project.mother_ideas?.length || 9} ideas</span>
                              <span>📝 {project.content_pieces?.length || 0} piezas</span>
                              <span className={`px-2 py-0.5 rounded ${project.step === 2 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {project.step === 2 ? '✓ Completo' : 'En progreso'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Eye className="w-4 h-4 mr-1" />
                            <span className="text-xs font-medium">Ver Todo</span>
                            <ChevronRight className="w-4 h-4" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {(filteredPlannerProjects.length > ITEMS_PER_PAGE || plannerSearch) && (
                  <PaginationControls 
                    currentPage={plannerPage} 
                    totalPages={plannerTotalPages} 
                    setPage={setPlannerPage}
                    totalItems={filteredPlannerProjects.length}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Contenido Individual */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Contenido Individual
                </CardTitle>
                <CardDescription>
                  Posts generados uno a uno: {individualContent.length} piezas
                </CardDescription>
              </div>
              {individualContent.length > 0 && (
                <div className="w-64">
                  <SearchInput 
                    value={individualSearch}
                    onChange={handleIndividualSearch}
                    placeholder="Buscar contenido..."
                  />
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
              </div>
            ) : individualContent.length === 0 ? (
              <div className="text-center py-6 bg-blue-50 rounded-lg">
                <FileText className="w-10 h-10 text-blue-300 mx-auto mb-2" />
                <p className="text-blue-600 text-sm">Aún no has generado contenido individual</p>
                <Button onClick={() => setActiveTab('single')} variant="outline" size="sm" className="mt-3">
                  Crear Primer Contenido
                </Button>
              </div>
            ) : filteredIndividualContent.length === 0 ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg">
                <Search className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-600 text-sm">No se encontraron resultados para "{individualSearch}"</p>
                <Button onClick={() => setIndividualSearch('')} variant="outline" size="sm" className="mt-3">
                  Limpiar búsqueda
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {paginatedIndividualContent.map((item, idx) => {
                    const getContentText = () => {
                      if (!item.content) return item.post_content || '';
                      if (typeof item.content === 'string') return item.content;
                      if (item.content.main_text) return item.content.main_text;
                      if (item.content.caption) return item.content.caption;
                      if (item.content.intro) return item.content.intro;
                      if (item.content.tweets) return item.content.tweets.map(t => t.text).join('\n');
                      return '';
                    };

                    return (
                      <Card key={idx} className="hover:shadow-md transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                                  {item.content_type?.replace('_', ' ') || 'contenido'}
                                </span>
                                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                                  {item.platform}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {new Date(item.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <p className="font-semibold text-slate-900 mb-1">{item.topic}</p>
                              {item.content?.hook && (
                                <p className="text-sm text-yellow-700 font-medium mb-1">🎯 {item.content.hook}</p>
                              )}
                              <p className="text-sm text-slate-600 line-clamp-3 whitespace-pre-wrap">
                                {getContentText()}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCopy(getContentText())}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                {(filteredIndividualContent.length > ITEMS_PER_PAGE || individualSearch) && (
                  <PaginationControls 
                    currentPage={individualPage} 
                    totalPages={individualTotalPages} 
                    setPage={setIndividualPage}
                    totalItems={filteredIndividualContent.length}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      {/* Project Detail Modal */}
      {renderProjectDetailModal()}
      
      {/* Page Header */}
      <div className="bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.href = '/dashboard'}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Generador de Contenido</h1>
            <p className="text-sm text-slate-600">Posts individuales o planificación mensual completa</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            onClick={() => setActiveTab('single')}
            variant={activeTab === 'single' ? 'default' : 'outline'}
          >
            <FileText className="w-4 h-4 mr-2" />
            Contenido Individual
          </Button>
          <Button
            onClick={() => setActiveTab('planner')}
            variant={activeTab === 'planner' ? 'default' : 'outline'}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Planificador 3-3-3
          </Button>
          <Button
            onClick={() => setActiveTab('history')}
            variant={activeTab === 'history' ? 'default' : 'outline'}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Historial ({history.length})
          </Button>
        </div>

        {activeTab === 'single' && renderSingleTab()}
        {activeTab === 'planner' && renderPlannerTab()}
        {activeTab === 'history' && renderHistoryTab()}
      </div>
    </div>
  );
};

export default ContentGenerator;
